/**
 * The one pagination block for the admin panel.
 *
 * Reference DOM (user, 2026-09-20 — every paginated report ships this):
 *
 *   div.row.pt-3 > div.col
 *     > div.dataTables_paginate.paging_simple_numbers.float-right
 *       > ul.pagination.pagination-rounded.mb-0            ← outer, styling only
 *         > ul[role=menubar][aria-label=Pagination].pagination.dataTables_paginate
 *             .paging_simple_numbers.my-0.b-pagination.justify-content-end
 *           li.page-item        «  ‹   (button, or span.page-link when disabled)
 *           li.page-item[.active]  1 2 3 …   (button[role=menuitemradio])
 *           li.page-item        ›  »
 *
 * The nested `ul` is the reference's own markup (bootstrap-vue's b-pagination
 * rendered inside the theme's `ul.pagination`), not a mistake — keep it.
 *
 * Every page number is rendered, the way bootstrap-vue does it: a window of
 * `limit` pages around the current one, with its ellipsis separator when the
 * list is longer. Before this, each report drew its own copy of this block and
 * most of them showed ONLY the active page, so a 6-page report gave no way to
 * jump to page 3.
 */

// Same default as bootstrap-vue's b-pagination.
const DEFAULT_LIMIT = 5;

/** The page numbers to show, bootstrap-vue style: a window of `limit` around current. */
function pageWindow(currentPage, totalPages, limit) {
  if (totalPages <= limit) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const half = Math.floor(limit / 2);
  let start = Math.max(1, currentPage - half);
  const end = Math.min(totalPages, start + limit - 1);
  start = Math.max(1, end - limit + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/** « ‹ › » — a button while it can be used, a disabled span once it cannot. */
function ArrowItem({ label, aria, disabled, page, onPageChange }) {
  return (
    <li role="presentation" aria-hidden={disabled ? 'true' : undefined} className={`page-item${disabled ? ' disabled' : ''}`}>
      {disabled ? (
        <span role="menuitem" aria-label={aria} aria-disabled="true" className="page-link">{label}</span>
      ) : (
        <button
          role="menuitem"
          type="button"
          tabIndex="-1"
          aria-label={aria}
          className="page-link"
          onClick={() => onPageChange(page)}
        >
          {label}
        </button>
      )}
    </li>
  );
}

function Pagination({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  limit = DEFAULT_LIMIT,
  // Reports wrap this in their own row/col; pass wrap={false} when the caller
  // already provides one.
  wrap = true,
}) {
  const total = Math.max(1, Number(totalPages) || 1);
  const page = Math.min(Math.max(1, Number(currentPage) || 1), total);
  const go = (p) => {
    const next = Math.min(Math.max(1, p), total);
    if (next !== page && typeof onPageChange === 'function') onPageChange(next);
  };

  const pages = pageWindow(page, total, limit);
  const firstShown = pages[0];
  const lastShown = pages[pages.length - 1];

  const ellipsis = (key) => (
    <li key={key} role="separator" className="page-item disabled bv-d-xs-down-none">
      <span className="page-link">…</span>
    </li>
  );

  const block = (
    <div className="dataTables_paginate paging_simple_numbers float-right">
      <ul className="pagination pagination-rounded mb-0">
        <ul
          role="menubar"
          aria-disabled="false"
          aria-label="Pagination"
          className="pagination dataTables_paginate paging_simple_numbers my-0 b-pagination justify-content-end"
        >
          <ArrowItem label="&laquo;" aria="Go to first page" disabled={page === 1} page={1} onPageChange={go} />
          <ArrowItem label="&lsaquo;" aria="Go to previous page" disabled={page === 1} page={page - 1} onPageChange={go} />
          {firstShown > 1 && ellipsis('lead')}
          {pages.map((p) => {
            const active = p === page;
            return (
              <li key={p} role="presentation" className={`page-item${active ? ' active' : ''}`}>
                <button
                  role="menuitemradio"
                  type="button"
                  aria-label={`Go to page ${p}`}
                  aria-checked={active ? 'true' : 'false'}
                  aria-posinset={p}
                  aria-setsize={total}
                  tabIndex={active ? '0' : '-1'}
                  className="page-link"
                  onClick={() => go(p)}
                >
                  {p}
                </button>
              </li>
            );
          })}
          {lastShown < total && ellipsis('trail')}
          <ArrowItem label="&rsaquo;" aria="Go to next page" disabled={page >= total} page={page + 1} onPageChange={go} />
          <ArrowItem label="&raquo;" aria="Go to last page" disabled={page >= total} page={total} onPageChange={go} />
        </ul>
      </ul>
    </div>
  );

  if (!wrap) return block;

  return (
    <div className="row pt-3">
      <div className="col">
        {block}
      </div>
    </div>
  );
}

export default Pagination;
